import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { setLoader } from "../Store/Reducers/UserReducer";
import LoginFun from "../Layout Component/Loginfun";
import { useContextMenu } from "../Layout Component/ContextMenu";
import { copyText, downloadText, icsFor, printHtml, tableHtml } from "../utils/printDoc";
import "./Appointment.css";

// ── Appointments ─────────────────────────────────────────────────────────────
//
// The clinical Appointments list, for the patient: When, Length, What,
// Clinician, Where, Status. Opens on today and future; Past and All and a date
// range are one click away, and the search looks at every column.
//
//   hover        — a card with the full detail of the appointment
//   click        — selects the row
//   double-click — opens the same detail card
//   right-click  — copy the details, add to my calendar, print

const FILTERS = [
  ["upcoming", "Today and future"],
  ["past", "Past"],
  ["all", "All"],
  ["range", "Date range"],
];

const blank = (v) => v === null || v === undefined || ["", "n/a", "null", "undefined"].includes(String(v).trim().toLowerCase());

// The portal API is not consistent about naming, so a field is found under any
// of its likely spellings.
function pick(a, keys) {
  for (const k of keys) {
    const hit = Object.keys(a).find((x) => x.toLowerCase() === k.toLowerCase());
    if (hit && !blank(a[hit])) return a[hit];
  }
  return null;
}

function toRow(a) {
  const start = moment(a.startTime);
  const end = a.endTime && moment(a.endTime).isValid() ? moment(a.endTime) : null;
  const minutes = Number(a.duration) > 0 ? Number(a.duration) : end ? end.diff(start, "minutes") : null;
  return {
    id: a.id || `${a.startTime}-${a.subject}`,
    raw: a,
    start,
    end,
    minutes,
    what: a.subject || a.appointmentlibraryitemName || "Appointment",
    type: pick(a, ["appointmentlibraryitemName", "appointmentType", "appointmentTypeName", "typeName"]),
    clinician: pick(a, ["clinicianName", "clinician", "staffName", "staff", "resourceName", "ownerName", "consultantName", "practitionerName", "doctorName", "nurseName", "owneridName"]),
    where: [pick(a, ["roomName", "room"]), pick(a, ["siteName", "site", "locationName", "location", "clinicName", "centreName"])].filter(Boolean).join(" · "),
    status: pick(a, ["bookingStatusName", "statusName", "status"]),
    description: a.description,
    notes: pick(a, ["patientNotes", "instructions", "preparation"]),
  };
}

const lengthText = (m) => (m == null || Number.isNaN(m) ? "—" : m >= 120 && m % 60 === 0 ? `${m / 60} h` : `${m} min`);

function detailLines(r) {
  return [
    ["What", r.what],
    ["Type", r.type],
    ["When", `${r.start.format("ddd DD-MMM-YYYY HH:mm")}${r.end ? ` – ${r.end.format("HH:mm")}` : ""}`],
    ["Length", lengthText(r.minutes)],
    ["Clinician / staff", r.clinician],
    ["Where", r.where],
    ["Status", r.status],
    ["Details", r.description],
    ["Notes", r.notes],
  ].filter(([, v]) => !blank(v));
}

const copyBlock = (r) => detailLines(r).map(([k, v]) => `${k}: ${v}`).join("\n");

export default function Appointment() {
  const dispatch = useDispatch();
  const url = process.env.REACT_APP_OXAR_API_URL;
  const logindata = LoginFun();
  const { show, menu } = useContextMenu();

  const [all, setAll] = useState(null);
  const [filter, setFilter] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(moment().subtract(1, "month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().add(3, "months").format("YYYY-MM-DD"));
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null); // { row, x, y }
  const [open, setOpen] = useState(null);   // row shown in the detail card

  const load = useCallback(async () => {
    const now = Math.round(new Date().getTime() / 1000);
    if (localStorage.getItem("ExpirretokenTime") < now) {
      Swal.fire({ title: "Session Expired", text: "Your session has been expired! Please login again", icon: "warning", confirmButtonText: "Okay" })
        .then(() => logindata.logout());
      return;
    }
    try {
      dispatch(setLoader(true));
      const id = localStorage.getItem("UserID");
      const res = await axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${id}`);
      setAll((res.data.content || []).map(toRow));
    } catch {
      setAll([]);
      toast.error("Your appointments could not be loaded. Please refresh to try again.");
    } finally {
      dispatch(setLoader(false));
    }
  }, [dispatch, url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const list = all || [];
    const today = moment().startOf("day");
    const term = search.trim().toLowerCase();
    const filtered = list.filter((r) => {
      if (filter === "upcoming" && r.start.isBefore(today)) return false;
      if (filter === "past" && !r.start.isBefore(today)) return false;
      if (filter === "range" && (r.start.isBefore(moment(from).startOf("day")) || r.start.isAfter(moment(to).endOf("day")))) return false;
      if (!term) return true;
      return [r.what, r.type, r.clinician, r.where, r.status, r.description, r.start.format("ddd DD/MM/YYYY HH:mm")]
        .filter(Boolean).join(" ").toLowerCase().includes(term);
    });
    // Coming up: soonest first. Looking back: most recent first.
    return filtered.sort((a, b) => (filter === "upcoming" ? a.start - b.start : b.start - a.start));
  }, [all, filter, search, from, to]);

  const counts = useMemo(() => {
    const today = moment().startOf("day");
    const list = all || [];
    return {
      upcoming: list.filter((r) => !r.start.isBefore(today)).length,
      past: list.filter((r) => r.start.isBefore(today)).length,
      all: list.length,
    };
  }, [all]);

  const addToCalendar = (r) => {
    downloadText(
      `appointment-${r.start.format("YYYYMMDD-HHmm")}.ics`,
      icsFor({ title: r.what, start: r.start.toDate(), end: r.end ? r.end.toDate() : null, location: r.where || "", description: [r.type, r.clinician && `With ${r.clinician}`, r.description].filter(Boolean).join(" – ") }),
    );
    toast.success("Calendar file downloaded. Open it to add the appointment.", { autoClose: 3000, hideProgressBar: true });
  };

  const printRows = (rows) => {
    const body = `<h2>Appointments</h2>${tableHtml(
      ["When", "Length", "What", "Clinician", "Where", "Status"],
      rows.map((r) => [r.start.format("ddd DD/MM/YYYY HH:mm"), lengthText(r.minutes), r.what + (r.type && r.type !== r.what ? ` (${r.type})` : ""), r.clinician || "—", r.where || "—", r.status || "—"]),
    )}`;
    if (!printHtml({ title: "Appointments", subtitle: FILTERS.find(([k]) => k === filter)[1], bodyHtml: body })) toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
  };

  const rowMenu = (e, r) => {
    setSelected(r.id);
    show(e, [
      { label: "View details", hint: "double-click", onClick: () => setOpen(r) },
      { divider: true },
      { label: "Copy appointment details", onClick: async () => { const ok = await copyText(copyBlock(r)); (ok ? toast.success : toast.error)(ok ? "Appointment details copied" : "Could not copy", { autoClose: 1800, hideProgressBar: true }); } },
      { label: "Copy date and time", onClick: async () => { await copyText(r.start.format("dddd D MMMM YYYY [at] HH:mm")); toast.success("Date and time copied", { autoClose: 1800, hideProgressBar: true }); } },
      { label: "Add to my calendar (.ics)", onClick: () => addToCalendar(r) },
      { divider: true },
      { label: "Print this appointment", onClick: () => printRows([r]) },
      { label: "Print the list", onClick: () => printRows(shown) },
    ]);
  };

  const patient = (() => { try { return JSON.parse(localStorage.getItem("UData")); } catch { return null; } })();

  return (
    <div className="ox-appts" onContextMenu={(e) => show(e, [
      { label: "Print the list", onClick: () => printRows(shown) },
      { label: "Refresh", onClick: load },
    ])}>
      <div className="ox-mh__banner">
        <div>
          <h2 className="ox-mh__title">Appointments</h2>
          <p className="ox-mh__sub">
            {patient && [`${patient.firstName || ""} ${patient.lastName || ""}`.trim(), patient.birthday && `DOB ${moment(patient.birthday).format("DD-MMM-YYYY")} (${patient.age})`, patient.gendercodeName].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>

      <section className="ox-card ox-appts__card">
        <div className="ox-appts__bar">
          <div className="ox-seg" role="tablist" aria-label="Which appointments">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={filter === key}
                className={`ox-seg__btn${filter === key ? " is-active" : ""}`} onClick={() => setFilter(key)}>
                {label}{counts[key] !== undefined && <span className="ox-seg__n">{counts[key]}</span>}
              </button>
            ))}
          </div>
          {filter === "range" && (
            <span className="ox-appts__range">
              <input type="date" className="form-control" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
              <span>to</span>
              <input type="date" className="form-control" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="To" />
            </span>
          )}
          <input type="search" className="form-control ox-appts__search" placeholder="Search appointments..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search appointments" />
          <span className="ox-hint">{all ? `${shown.length} of ${all.length}` : "Loading..."}</span>
          <span className="ox-appts__tools">
            <button type="button" className="ox-btn" onClick={() => printRows(shown)} disabled={!shown.length}>Print</button>
            <button type="button" className="ox-btn" onClick={load}>Refresh</button>
          </span>
        </div>

        <div className="ox-appts__scroll">
          <table className="ox-appts__table">
            <thead>
              <tr><th>When</th><th>Length</th><th>What</th><th>Clinician</th><th>Where</th><th>Status</th></tr>
            </thead>
            <tbody>
              {all && shown.length === 0 && (
                <tr><td colSpan={6} className="ox-appts__empty">
                  {all.length === 0 ? "You have no appointments." : filter === "upcoming" ? "No upcoming appointments. Choose Past or All to see the rest." : "No appointments match."}
                </td></tr>
              )}
              {shown.map((r) => {
                const isPast = r.start.isBefore(moment().startOf("day"));
                return (
                  <tr
                    key={r.id}
                    tabIndex={0}
                    className={`${selected === r.id ? "is-selected" : ""}${isPast ? " is-past" : ""}`}
                    onClick={() => setSelected(r.id)}
                    onDoubleClick={() => setOpen(r)}
                    onKeyDown={(e) => { if (e.key === "Enter") setOpen(r); }}
                    onContextMenu={(e) => rowMenu(e, r)}
                    onMouseEnter={(e) => setHover({ row: r, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))}
                    onMouseLeave={() => setHover(null)}
                  >
                    <td className="ox-appts__when"><strong>{r.start.format("ddd DD/MM/YYYY")}</strong><span>{r.start.format("HH:mm")}</span></td>
                    <td>{lengthText(r.minutes)}</td>
                    <td className="ox-appts__what"><strong>{r.what}</strong>{r.type && r.type !== r.what && <span>{r.type}</span>}</td>
                    <td>{r.clinician || "—"}</td>
                    <td>{r.where || "—"}</td>
                    <td>{r.status || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="ox-hint ox-appts__foot">Hover for details &middot; double-click to open &middot; right-click to copy, add to your calendar or print.</p>
      </section>

      {hover && !open && (
        <div className="ox-tip" style={{ left: Math.min(hover.x + 16, window.innerWidth - 340), top: Math.min(hover.y + 16, window.innerHeight - 220) }} role="tooltip">
          {detailLines(hover.row).map(([k, v]) => (<div key={k}><span>{k}</span><strong>{v}</strong></div>))}
        </div>
      )}

      {open && (
        <div className="ox-modal" role="dialog" aria-modal="true" aria-label="Appointment details" onClick={() => setOpen(null)}>
          <div className="ox-modal__box" onClick={(e) => e.stopPropagation()}>
            <header><h3>{open.what}</h3><button type="button" className="ox-link" onClick={() => setOpen(null)} aria-label="Close">Close</button></header>
            <dl>
              {detailLines(open).map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v}</dd></div>))}
            </dl>
            <footer>
              <button type="button" className="ox-btn" onClick={() => copyText(copyBlock(open)).then(() => toast.success("Copied", { autoClose: 1500, hideProgressBar: true }))}>Copy</button>
              <button type="button" className="ox-btn" onClick={() => addToCalendar(open)}>Add to my calendar</button>
              <button type="button" className="ox-btn" onClick={() => printRows([open])}>Print</button>
            </footer>
          </div>
        </div>
      )}
      {menu}
    </div>
  );
}
